// v19.0 — DAW auth gate. Renders a full-screen "Sign in to enter the DAW"
// prompt when an unauthenticated visitor navigates to /daw (or clicks the
// TopBar "CROW DAW" link). Once the user completes login/registration via
// the AuthModal, `useAuth().user` flips truthy and the wrapped children
// (the real DAWPage) render.
//
// v19.1 HARD ROUTE GUARD — Belt-and-braces defence added:
//   • Synchronous localStorage token probe BEFORE AuthContext resolves.
//     If no token exists, we short-circuit straight to the gate without
//     ever letting <DAWPage /> mount — no race, no flash.
//   • While AuthContext is validating a present-but-unverified token,
//     we render a loading spinner instead of DAW children.
//   • Only when `ready === true` AND `user` is a real object do we
//     forward children.
// This guarantees zero DAW workspace render for guests, expired tokens,
// or invalid tokens.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, SpinnerGap } from "@phosphor-icons/react";
import { useAuth } from "./AuthContext";
import AuthModal from "./AuthModal";
import GlobalNav from "../components/GlobalNav";

// Synchronous first-paint check. Reading localStorage is safe during
// render (no side effects) and lets us decide the initial render tree
// BEFORE AuthContext's useEffect has even scheduled its refresh call.
function hasClientToken() {
  try { return !!localStorage.getItem("crow_token"); } catch { return false; }
}

export default function RequireDawAuth({ children }) {
  const { user, ready } = useAuth();
  const [openAuth, setOpenAuth] = useState(false);
  const nav = useNavigate();

  // Snapshot the token presence at first render so we can commit to the
  // "no token → gate immediately" branch without waiting on AuthContext.
  const [tokenAtMount] = useState(hasClientToken);

  // Auto-open the auth modal on mount for guests so the CTA is not
  // buried — they should be able to sign up in one click.
  useEffect(() => {
    if (!tokenAtMount) { setOpenAuth(true); return; }
    if (ready && !user) setOpenAuth(true);
  }, [ready, user, tokenAtMount]);

  // Fast path: no token in localStorage at mount AND AuthContext still
  // hasn't produced a `user` → gate synchronously. Never mounts
  // <DAWPage />, so no autosave/audio/DB side-effects fire.
  // v19.15 FIX — Also bail out of the fast-path once `user` is truthy
  // (a successful in-page login writes crow_token AND populates
  // AuthContext.user). Previously the snapshot froze `tokenAtMount`
  // to false forever, so the gate + modal stayed rendered even after
  // a successful login — the user had to hard-reload to reach /daw.
  if (!tokenAtMount && !user) {
    return <GateScreen nav={nav} openAuth={openAuth} setOpenAuth={setOpenAuth} />;
  }

  // Token exists but AuthContext is still validating it (fetching
  // /auth/me). Show a spinner — do NOT render <DAWPage /> yet, because
  // the token might turn out to be expired/invalid.
  if (!ready) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#0a0a0a] text-white" data-testid="daw-auth-loading">
        <SpinnerGap size={32} weight="bold" className="animate-spin text-emerald-300" />
      </div>
    );
  }

  // Token was present but AuthContext rejected it (invalid / expired /
  // server said 401). Gate the same way we do for guests.
  if (!user) {
    return <GateScreen nav={nav} openAuth={openAuth} setOpenAuth={setOpenAuth} />;
  }

  // Authenticated — safe to render the DAW.
  return children;
}

function GateScreen({ nav, openAuth, setOpenAuth }) {
  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center bg-[#0a0a0a] text-white relative overflow-hidden"
      data-testid="daw-auth-gate"
    >
      {/* Subtle grid backdrop mirroring the landing page vibe */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.05) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,0.05) 2px, transparent 2px)",
          backgroundSize: "80px 80px",
        }}
      />
      <button
        onClick={() => nav("/")}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/40 hover:text-white text-sm font-mono transition-colors z-10"
        data-testid="daw-gate-back-home"
      >
        <ArrowLeft size={16} weight="bold" /> Back to home
      </button>
      <div className="absolute top-5 right-6 z-10"><GlobalNav /></div>
      <div className="relative z-10 text-center max-w-md px-6">
        <div className="mx-auto mb-6 w-16 h-16 rounded-full border border-emerald-500/30 bg-emerald-500/10 flex items-center justify-center">
          <Lock size={26} weight="fill" className="text-emerald-300" />
        </div>
        <h1
          className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-3"
          data-testid="daw-gate-heading"
        >
          Sign in to enter the DAW
        </h1>
        <p className="text-white/50 text-sm mb-8">
          The C.R.O.W. DAW is for signed-in producers. Your first beat is
          on the house — after that, unlock unlimited projects with the
          Producer Package.
        </p>
        <button
          onClick={() => setOpenAuth(true)}
          className="px-8 py-3 rounded-full font-display font-extrabold text-base tracking-wide transition-all"
          style={{
            background: "linear-gradient(180deg,#fff,#d4d4d4)",
            color: "#0a0a0a",
            boxShadow: "0 0 0 1.5px #34d399, 0 0 32px rgba(52,211,153,0.55), 0 6px 16px rgba(0,0,0,0.6)",
          }}
          data-testid="daw-gate-signin-btn"
        >
          Sign in · Sign up
        </button>
      </div>
      <AuthModal open={openAuth} onClose={() => setOpenAuth(false)} />
    </div>
  );
}
